import { faCircleUser, faUserCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArchive,
    faArrowTurnLeft,
    faDownload,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import {
    type QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    EmissionEventFullRequest,
    EmissionRecordsListRequest,
} from "../../apiClient/generated";
import { usePermission, useUser } from "../../auth";
import {
    useAccountsApiClient,
    useAdminApiClient,
    useDataProvidersApi,
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
    useGeoDataApi,
} from "../../hooks";
import { ROUTES } from "../../routes";
import {
    type BulkActionsConfirmationProps,
    BulkActionsMenu,
    type BulkActionsMenuVariant,
    type BulkActionsProps,
    BulkFailuresDownloadButton,
    BulkFailuresPreview,
    BulkFailuresTruncatedNote,
    BulkMenuItem,
    BulkScopeSelector,
    type FilterSummaryItem,
} from "../../ui/BulkTableAction";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { FilteringDropdown } from "../../ui/Inputs";
import { FullscreenModal, Modal } from "../../ui/Modals";
import { formatDateTime } from "../../utils/dates";
import {
    EMISSIONS_TABLE_PREFIX,
    invalidateKeyPrefix,
} from "../../utils/queryInvalidation";
import { DataExportStatus } from "../dashboards/DataExport";
import { UserDropdown } from "../EventsV2/UserDropdown";

const invalidateEmissions = (queryClient: QueryClient) => {
    invalidateKeyPrefix(queryClient, EMISSIONS_TABLE_PREFIX);
    queryClient.invalidateQueries({ queryKey: ["Event"] });
    queryClient.invalidateQueries({ queryKey: ["emissionRecord"] });
    // Stats panel rendered above the table, and the main record resolved for
    // LINKED rows inside it.
    queryClient.invalidateQueries({ queryKey: ["emissionRecordStats"] });
    queryClient.invalidateQueries({ queryKey: ["emissionRecordForEvent"] });
};

const invalidateEmissionsStatus = (queryClient: QueryClient) => {
    invalidateEmissions(queryClient);
    queryClient.invalidateQueries({ queryKey: ["emissionsRelatedToEvent"] });
    queryClient.invalidateQueries({ queryKey: ["formsForEvent"] });
    queryClient.invalidateQueries({ queryKey: ["event"] });
};

// Filters come from emissionTableFiltersAtom and are spread into the bulk
// endpoints, so they carry the emission list-request shape.
type EmissionBulkProps = BulkActionsProps<EmissionRecordsListRequest>;
type EmissionBulkModalProps =
    BulkActionsConfirmationProps<EmissionRecordsListRequest> & {
        filterSummary?: FilterSummaryItem[];
    };

const IGNORED_FILTER_KEYS: string[] = ["ordering"];

const humanizeFilterKey = (key: string) =>
    key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (character) => character.toUpperCase());

const FILTER_LABELS: Partial<Record<keyof EmissionRecordsListRequest, string>> =
    {
        assignedTo: "Assignee",
        bridgerDetectionId: "Bridger Detection ID",
        bridgerEmissionLocationId: "Bridger Emission Location ID",
        bridgerEmitterHeightFtMax: "Bridger Emitter Height (ft) max",
        bridgerEmitterHeightFtMin: "Bridger Emitter Height (ft) min",
        bridgerEquipmentId: "Bridger Equipment ID",
        bridgerEquipmentType: "Bridger Equipment Type",
        bridgerLocation: "Bridger Location",
        bridgerPersistence: "Bridger Persistence",
        bridgerSiteName: "Bridger Site Name",
        concentrationMax: "Concentration max",
        concentrationMin: "Concentration min",
        createdAtAfter: "Match Date from",
        createdAtBefore: "Match Date to",
        dataPointCreatedAtAfter: "Import Date from",
        dataPointCreatedAtBefore: "Import Date to",
        detectedRateMax: "Detection rate max",
        detectedRateMin: "Detection rate min",
        detectionDateRangeAfter: "Detection Date (UTC) from",
        detectionDateRangeBefore: "Detection Date (UTC) to",
        eventStatus: "Status",
        eventUpdatedAtAfter: "Last Updated from",
        eventUpdatedAtBefore: "Last Updated to",
        locationPreset: "Location",
        locationWithin: "Location",
        providerWithSource: "Provider",
        search: "Search",
    };

const formatProviderFilter = (
    value: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    providerName: string | undefined,
) => {
    const sources = value.se?.filter(Boolean);
    const name = providerName ?? value.id;
    return sources?.length ? `${name} (${sources.join(", ")})` : name;
};

const useFilterSummary = (filters: EmissionRecordsListRequest) => {
    const accountsApiClient = useAccountsApiClient();
    const dataProvidersApiClient = useDataProvidersApi();
    const geoDataApiClient = useGeoDataApi();
    const assigneeIds = filters.assignedTo ?? [];
    const locationPresetIds = filters.locationPreset ?? [];
    const providerFilters = useMemo(() => {
        if (Array.isArray(filters.providerWithSource)) {
            return filters.providerWithSource;
        }
        if (typeof filters.providerWithSource === "string") {
            try {
                return JSON.parse(filters.providerWithSource);
            } catch {
                return [];
            }
        }
        return [];
    }, [filters.providerWithSource]);
    const usersQuery = useQuery({
        queryKey: ["usersForBulkFilterSummary"],
        queryFn: () => accountsApiClient.accountsUsersList(),
        enabled: assigneeIds.length > 0,
        staleTime: Infinity,
    });
    const providersQuery = useQuery({
        queryKey: ["dataProvidersForBulkFilterSummary"],
        queryFn: () => dataProvidersApiClient.dataProvidersList(),
        enabled: providerFilters.length > 0,
        staleTime: Infinity,
    });
    const locationPresetsQuery = useQuery({
        queryKey: ["geoFilterItems"],
        queryFn: async () => {
            const response = await geoDataApiClient.geodataItemsList({
                pageSize: 1000,
            });
            return response.results;
        },
        enabled: locationPresetIds.length > 0,
        staleTime: Infinity,
    });

    return useMemo(() => {
        const summary: FilterSummaryItem[] = [];
        for (const [key, rawValue] of Object.entries(filters) as Array<
            [keyof EmissionRecordsListRequest, unknown]
        >) {
            const label = FILTER_LABELS[key] ?? humanizeFilterKey(key);
            if (
                IGNORED_FILTER_KEYS.includes(key) ||
                rawValue === undefined ||
                rawValue === null ||
                rawValue === ""
            )
                continue;

            if (key === "assignedTo" && Array.isArray(rawValue)) {
                summary.push({
                    label,
                    value: rawValue
                        .map((id) => {
                            const user = usersQuery.data?.find(
                                (candidate) =>
                                    String(candidate.id) === String(id),
                            );
                            return user
                                ? `${user.firstName} ${user.lastName}`
                                : String(id);
                        })
                        .join(", "),
                });
            } else if (key === "providerWithSource") {
                summary.push({
                    label,
                    value: providerFilters
                        .map((provider) =>
                            formatProviderFilter(
                                provider,
                                providersQuery.data?.results.find(
                                    (candidate) => candidate.id === provider.id,
                                )?.name,
                            ),
                        )
                        .join(", "),
                });
            } else if (key === "locationPreset" && Array.isArray(rawValue)) {
                const names = rawValue
                    .map(
                        (id) =>
                            locationPresetsQuery.data?.find(
                                (preset) => preset.id === id,
                            )?.name,
                    )
                    .filter((name): name is string => Boolean(name));
                summary.push({
                    label,
                    value: names.length
                        ? names.join(", ")
                        : rawValue.join(", "),
                });
            } else if (key === "locationWithin") {
                summary.push({ label, value: "Custom area" });
            } else if (
                (key === "detectedRateMin" || key === "detectedRateMax") &&
                typeof rawValue === "number"
            ) {
                // The table converts rates from kg/h to g/h for the API.
                summary.push({ label, value: String(rawValue / 1000) });
            } else if (rawValue instanceof Date) {
                summary.push({ label, value: formatDateTime(rawValue) });
            } else if (Array.isArray(rawValue)) {
                summary.push({ label, value: rawValue.join(", ") });
            } else {
                summary.push({ label, value: String(rawValue) });
            }
        }
        return summary;
    }, [
        filters,
        locationPresetsQuery.data,
        providerFilters,
        providersQuery.data,
        usersQuery.data,
    ]);
};

const emissionFailureLink = (id: string) =>
    `${ROUTES.DASHBOARD_ALL}?selectedEvent=${id}`;

const BulkAssignModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();
    const adminApiClient = useAdminApiClient();
    const { isStaff, company: currentUserCompany } = useUser();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    // Mutation control
    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );
    const [selectedCompany, setSelectedCompany] = useState<number>(null);
    const [userId, setUserId] = useState<number>(null);

    const companiesQuery = useQuery({
        queryKey: ["staffCompanyList"],
        queryFn: async () => {
            return await adminApiClient.adminAccountsCompaniesList();
        },
        enabled: isStaff,
    });

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkAssignCreate({
                bulkAssignRequest: { userId: String(userId) },
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            invalidateEmissions(queryClient);
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">Bulk assign</h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Select the user you want to assign{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""} to:
                    </p>
                    {isStaff && (
                        <>
                            <p className="text-sm text-gray-600 mb-1 block">
                                Company
                                <sup>(admin only)</sup>
                            </p>
                            <FilteringDropdown
                                value={selectedCompany}
                                onChange={(c) => {
                                    setSelectedCompany(c as number);
                                    setUserId(null);
                                }}
                                options={
                                    companiesQuery.data?.map((c) => ({
                                        id: c.id,
                                        displayName: c.name,
                                        searchString: c.name.toLowerCase(),
                                    })) ?? []
                                }
                            />
                            <div className="mt-3" />
                        </>
                    )}
                    <UserDropdown
                        value={userId}
                        onChange={(id) => setUserId(id)}
                        company={
                            isStaff ? selectedCompany : currentUserCompany?.id
                        }
                    />
                    {props.allSelected && (
                        <div className="mt-3">
                            <BulkScopeSelector
                                allSelected={assignToAllFiltered}
                                allSelectedCount={
                                    allSelectedCountQuery.data?.count
                                }
                                filterSummary={props.filterSummary}
                                noun="emissions"
                                onChange={setAssignedToAllFiltered}
                                selectedCount={props.selectedIds.length}
                            />
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                !userId ||
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Assign to{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Assigning emissions...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully assigned {bulkActionMutation.data.ok}{" "}
                        emission{bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be assigned. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-assign"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkAssignReviewerModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();
    const adminApiClient = useAdminApiClient();
    const { isStaff, company: currentUserCompany } = useUser();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    // Mutation control
    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );
    const [selectedCompany, setSelectedCompany] = useState<number>(null);
    const [userId, setUserId] = useState<number>(null);

    const companiesQuery = useQuery({
        queryKey: ["staffCompanyList"],
        queryFn: async () => {
            return await adminApiClient.adminAccountsCompaniesList();
        },
        enabled: isStaff,
    });

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkAssignReviewerCreate({
                bulkAssignReviewerRequest: { userId: String(userId) },
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            invalidateEmissions(queryClient);
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">
                Bulk assign coordinator
            </h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Select the reviewer you want to assign{" "}
                        {affectedCount ?? "..."} emission
                        {affectedCount !== 1 ? "s" : ""} to:
                    </p>
                    {isStaff && (
                        <>
                            <p className="text-sm text-gray-600 mb-1 block">
                                Company
                                <sup>(admin only)</sup>
                            </p>
                            <FilteringDropdown
                                value={selectedCompany}
                                onChange={(c) => {
                                    setSelectedCompany(c as number);
                                    setUserId(null);
                                }}
                                options={
                                    companiesQuery.data?.map((c) => ({
                                        id: c.id,
                                        displayName: c.name,
                                        searchString: c.name.toLowerCase(),
                                    })) ?? []
                                }
                            />
                            <div className="mt-3" />
                        </>
                    )}
                    <UserDropdown
                        value={userId}
                        onChange={(id) => setUserId(id)}
                        company={
                            isStaff ? selectedCompany : currentUserCompany?.id
                        }
                        hasApprovalPermission={true}
                    />
                    {props.allSelected && (
                        <div className="mt-3">
                            <BulkScopeSelector
                                allSelected={assignToAllFiltered}
                                allSelectedCount={
                                    allSelectedCountQuery.data?.count
                                }
                                filterSummary={props.filterSummary}
                                noun="emissions"
                                onChange={setAssignedToAllFiltered}
                                selectedCount={props.selectedIds.length}
                            />
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={!userId || bulkActionMutation.isPending}
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Assign coordinator to {affectedCount ?? "..."}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Assigning reviewer...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully assigned reviewer to{" "}
                        {bulkActionMutation.data.ok} emission
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be assigned. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresTruncatedNote
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-assign-reviewer"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkArchiveModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkArchiveCreate({
                emissionEventFullRequest: {} as EmissionEventFullRequest,
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            invalidateEmissionsStatus(queryClient);
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">Bulk archive</h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to archive{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""}?
                    </p>
                    {props.allSelected && (
                        <BulkScopeSelector
                            allSelected={assignToAllFiltered}
                            allSelectedCount={allSelectedCountQuery.data?.count}
                            filterSummary={props.filterSummary}
                            noun="emissions"
                            onChange={setAssignedToAllFiltered}
                            selectedCount={props.selectedIds.length}
                        />
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Archive{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Archiving emissions...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully archived {bulkActionMutation.data.ok}{" "}
                        emission{bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be archived. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-archive"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkMoveToInProgressModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkMoveToInProgressCreate({
                emissionEventFullRequest: {} as EmissionEventFullRequest,
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            invalidateEmissionsStatus(queryClient);
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">
                Move to in progress
            </h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to move{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""} back to in progress?
                    </p>
                    {props.allSelected && (
                        <BulkScopeSelector
                            allSelected={assignToAllFiltered}
                            allSelectedCount={allSelectedCountQuery.data?.count}
                            filterSummary={props.filterSummary}
                            noun="emissions"
                            onChange={setAssignedToAllFiltered}
                            selectedCount={props.selectedIds.length}
                        />
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Move{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Moving emissions to in progress...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully moved {bulkActionMutation.data.ok} emission
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""} to in
                        progress.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be moved. Download the CSV to see the
                                reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-move-in-progress"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkExportModal = (props: EmissionBulkModalProps) => {
    const apiClient = useEmissionRecordsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return eventsApiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [exportRequested, setExportRequested] = useState(false);
    const [exportId, setExportId] = useState<string>(undefined);
    const [errorMessage, setErrorMessage] = useState<string>(null);

    const onSubmit = async () => {
        setExportRequested(true);
        setErrorMessage(null);
        try {
            const response =
                await apiClient.emissionRecordsGenerateCsvReportCreate({
                    ...(assignToAllFiltered
                        ? props.filters
                        : { ids: props.selectedIds }),
                });
            if (response.status === "no_items") {
                setErrorMessage(
                    "No emission record data found for selected filters.",
                );
            } else if (response.status === "already_in_progress") {
                setErrorMessage(
                    "You already requested a data export for the given filters.",
                );
            } else {
                setExportId(response.dataDownloadId);
                props.onFinalizeAction();
            }
        } catch {
            setErrorMessage("Error while generating report. Please try again.");
        }
        setExportRequested(false);
    };

    return (
        <>
            <Modal
                visible={props.visible && !exportId}
                onClose={exportRequested ? undefined : props.onClose}
                size="sm"
            >
                <h2 className="text-lg font-semibold mb-4 mt-2">
                    Export as CSV
                </h2>
                {!exportRequested && (
                    <>
                        <p className="text-sm text-gray-600 mb-3">
                            Choose what goes into the file.
                        </p>
                        {props.allSelected && (
                            <BulkScopeSelector
                                allSelected={assignToAllFiltered}
                                allSelectedCount={
                                    allSelectedCountQuery.data?.count
                                }
                                filterSummary={props.filterSummary}
                                noun="emissions"
                                onChange={setAssignedToAllFiltered}
                                selectedCount={props.selectedIds.length}
                            />
                        )}
                        {errorMessage && (
                            <p className="text-sm text-red-600 mt-3">
                                <span className="font-bold">Error:</span>{" "}
                                {errorMessage}
                            </p>
                        )}
                        <div className="flex gap-2 mt-5">
                            <PrimaryButton
                                type="button"
                                onClick={onSubmit}
                                disabled={
                                    allSelectedCountQuery.isLoading &&
                                    assignToAllFiltered
                                }
                            >
                                Export{" "}
                                {affectedCount ?? (
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        className="animate-spin"
                                    />
                                )}{" "}
                                emission
                                {affectedCount !== 1 ? "s" : ""}
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                onClick={props.onClose}
                            >
                                Cancel
                            </SecondaryButton>
                        </div>
                    </>
                )}
                {exportRequested && (
                    <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin h-6"
                        />
                        Requesting export...
                    </div>
                )}
            </Modal>
            <FullscreenModal
                visible={!!exportId}
                onClose={() => {
                    setExportId(undefined);
                    props.onClose();
                }}
                title="Export Emission Data as CSV"
            >
                <DataExportStatus
                    exportId={exportId}
                    onClose={() => {
                        setExportId(undefined);
                        props.onClose();
                    }}
                />
            </FullscreenModal>
        </>
    );
};

export const BulkActions = ({
    variant,
    ...props
}: EmissionBulkProps & { variant?: BulkActionsMenuVariant }) => {
    const { isStaff } = useUser();
    const canEdit = usePermission("emissionEdit");
    const canApprove = usePermission("emissionEventApprove");
    const canArchive = usePermission("emissionEventArchive");
    const canExport = usePermission("emissionExport");
    const [showBulkModal, setShowBulkModal] = useState<
        "ASSIGN" | "ASSIGN_REVIEWER" | "ARCHIVE" | "IN_PROGRESS" | "EXPORT"
    >(null);
    const filterSummary = useFilterSummary(props.filters);

    const canAssign = canEdit || isStaff;
    const canAssignReviewer = canApprove || isStaff;
    const canArchiveAction = canArchive || isStaff;
    const canMoveInProgress = canEdit || isStaff;
    const canExportAction = canExport || isStaff;

    const hasAnyAction =
        canAssign ||
        canAssignReviewer ||
        canArchiveAction ||
        canMoveInProgress ||
        canExportAction;
    const bulkActionsDisabled = props.selectedIds.length === 0 || !hasAnyAction;

    return (
        <>
            <BulkActionsMenu disabled={bulkActionsDisabled} variant={variant}>
                <BulkMenuItem
                    icon={faCircleUser}
                    label="Assign to user"
                    enabled={canAssign}
                    disabledTitle="You don't have permission to edit emission events."
                    onClick={() => setShowBulkModal("ASSIGN")}
                />
                {canAssignReviewer && (
                    <BulkMenuItem
                        icon={faUserCheck}
                        label="Assign coordinator"
                        enabled={canAssignReviewer}
                        disabledTitle="You don't have permission to assign coordinators."
                        onClick={() => setShowBulkModal("ASSIGN_REVIEWER")}
                    />
                )}
                <BulkMenuItem
                    icon={faArchive}
                    label="Archive events"
                    enabled={canArchiveAction}
                    disabledTitle="You don't have permission to archive emission events."
                    onClick={() => setShowBulkModal("ARCHIVE")}
                />
                <BulkMenuItem
                    icon={faArrowTurnLeft}
                    label="Back to in progress"
                    enabled={canMoveInProgress}
                    disabledTitle="You don't have permission to edit emission events."
                    onClick={() => setShowBulkModal("IN_PROGRESS")}
                />
                <BulkMenuItem
                    icon={faDownload}
                    label="Export as CSV"
                    enabled={canExportAction}
                    disabledTitle="You don't have permission to export emission data."
                    onClick={() => setShowBulkModal("EXPORT")}
                />
            </BulkActionsMenu>
            {showBulkModal === "ASSIGN" && (
                <BulkAssignModal
                    {...props}
                    filterSummary={filterSummary}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ASSIGN"}
                />
            )}
            {showBulkModal === "ASSIGN_REVIEWER" && (
                <BulkAssignReviewerModal
                    {...props}
                    filterSummary={filterSummary}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ASSIGN_REVIEWER"}
                />
            )}
            {showBulkModal === "ARCHIVE" && (
                <BulkArchiveModal
                    {...props}
                    filterSummary={filterSummary}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ARCHIVE"}
                />
            )}
            {showBulkModal === "IN_PROGRESS" && (
                <BulkMoveToInProgressModal
                    {...props}
                    filterSummary={filterSummary}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "IN_PROGRESS"}
                />
            )}
            {showBulkModal === "EXPORT" && (
                <BulkExportModal
                    {...props}
                    filterSummary={filterSummary}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "EXPORT"}
                />
            )}
        </>
    );
};
